/**
 * Register Page Component
 */

import styled from '@emotion/styled';
import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { authService } from '@/lib/services/auth.service';

const PageContainer = styled.div`
  display: flex;
  min-height: calc(100vh - 200px);

  @media (max-width: ${({ theme }) => theme.mediaQueries.md}) {
    flex-direction: column;
  }
`;

const ImageSection = styled.div`
  flex: 1;
  background: linear-gradient(135deg, #f8d7d3 0%, #f5c6c0 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  position: relative;
  overflow: hidden;

  @media (max-width: ${({ theme }) => theme.mediaQueries.md}) {
    min-height: 400px;
  }
`;

const ProductImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: center;
`;

const FormSection = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: ${({ theme }) => theme.space['3xl']};
  background: ${({ theme }) => theme.colors.background.secondary};

  @media (max-width: ${({ theme }) => theme.mediaQueries.md}) {
    padding: ${({ theme }) => theme.space.xl};
  }
`;

const FormContainer = styled.div`
  width: 100%;
  max-width: 550px;
  background: ${({ theme }) => theme.colors.background.primary};
  padding: ${({ theme }) => theme.space['3xl']};
  border-radius: ${({ theme }) => theme.radius.regular};

  @media (max-width: ${({ theme }) => theme.mediaQueries.md}) {
    padding: ${({ theme }) => theme.space.xl};
  }
`;

const Title = styled.h1`
  margin-bottom: ${({ theme }) => theme.space['2xl']};
  text-align: left;
  font-size: ${({ theme }) => theme.typography.variants.heading3.fontSize}px;
  font-weight: ${({ theme }) => theme.typography.config.weight.bold};
  color: ${({ theme }) => theme.colors.text.primary};
  line-height: ${({ theme }) => theme.typography.variants.heading3.lineHeight};
  margin-top: 0;
`;

const NameRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: ${({ theme }) => theme.space.lg};
`;

const InputGroup = styled.div`
  margin-bottom: ${({ theme }) => theme.space.xl};
`;

const Label = styled.label`
  display: block;
  margin-bottom: ${({ theme }) => theme.space.md};
  font-size: ${({ theme }) => theme.typography.variants['body-regular'].fontSize}px;
  font-weight: ${({ theme }) => theme.typography.config.weight.bold};
  color: ${({ theme }) => theme.colors.text.primary};
  font-family: inherit;
`;

const Input = styled.input`
  width: 100%;
  padding: ${({ theme }) => theme.space.md} ${({ theme }) => theme.space.lg};
  border: 1px solid ${({ theme }) => theme.colors.border.color};
  border-radius: ${({ theme }) => theme.radius.small};
  font-size: ${({ theme }) => theme.typography.variants['body-regular'].fontSize}px;
  font-family: inherit;
  transition: all 0.2s;
  background: ${({ theme }) => theme.colors.background.primary};
  color: ${({ theme }) => theme.colors.text.primary};

  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.colors.text.primary};
    box-shadow: none;
  }

  &::placeholder {
    color: ${({ theme }) => theme.colors.text.tertiary};
  }
`;

const SubmitButton = styled.button`
  width: 100%;
  background-color: ${({ theme }) => theme.colors.text.primary};
  color: ${({ theme }) => theme.colors.background.primary};
  padding: ${({ theme }) => theme.space.md} ${({ theme }) => theme.space.xl};
  font-size: ${({ theme }) => theme.typography.variants['body-regular'].fontSize}px;
  font-weight: ${({ theme }) => theme.typography.config.weight.bold};
  border: none;
  border-radius: ${({ theme }) => theme.radius.small};
  cursor: pointer;
  transition: all 0.2s;
  font-family: inherit;
  letter-spacing: 0.5px;

  &:hover {
    opacity: 0.9;
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const LoginText = styled.div`
  margin-top: ${({ theme }) => theme.space.lg};
  font-size: ${({ theme }) => theme.typography.variants['body-small'].fontSize}px;
  color: ${({ theme }) => theme.colors.text.primary};
  font-family: inherit;
`;

const LoginLink = styled(Link)`
  color: ${({ theme }) => theme.colors.text.primary};
  text-decoration: underline;
  cursor: pointer;

  &:hover {
    color: ${({ theme }) => theme.colors.text.secondary};
  }
`;

const ErrorMessage = styled.div`
  width: 100%;
  padding: ${({ theme }) => theme.space.md};
  background: ${({ theme }) => theme.colors.semantic.urgent.background};
  color: ${({ theme }) => theme.colors.semantic.urgent.color};
  border-radius: ${({ theme }) => theme.radius.small};
  font-size: ${({ theme }) => theme.typography.variants['body-small'].fontSize}px;
  font-family: inherit;
  margin-bottom: ${({ theme }) => theme.space.lg};
  text-align: center;
`;

const FieldError = styled.span`
  display: block;
  color: ${({ theme }) => theme.colors.semantic.urgent.color};
  font-size: ${({ theme }) => theme.typography.variants['body-small'].fontSize}px;
  margin-top: ${({ theme }) => theme.space.sm};
`;

interface FormErrors {
  firstName?: string;
  lastName?: string;
  email?: string;
  password?: string;
  confirmPassword?: string;
}

function validate(
  firstName: string,
  lastName: string,
  email: string,
  password: string,
  confirmPassword: string
): FormErrors {
  const errors: FormErrors = {};

  if (!firstName.trim()) errors.firstName = 'First name is required';
  if (!lastName.trim()) errors.lastName = 'Last name is required';

  if (!email.trim()) {
    errors.email = 'Email is required';
  } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
    errors.email = 'Please enter a valid email address';
  }

  if (!password) {
    errors.password = 'Password is required';
  } else if (password.length < 8) {
    errors.password = 'Password must be at least 8 characters';
  }

  if (password && password !== confirmPassword) {
    errors.confirmPassword = 'Passwords do not match';
  }

  return errors;
}

export function RegisterPage() {
  const navigate = useNavigate();
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<FormErrors>({});

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    const errors = validate(firstName, lastName, email, password, confirmPassword);
    setFieldErrors(errors);
    if (Object.keys(errors).length > 0) return;

    setIsLoading(true);

    try {
      await authService.register({
        email,
        password,
        first_name: firstName.trim(),
        last_name: lastName.trim(),
      });
      navigate('/', { replace: true });
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Registration failed';
      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <PageContainer>
      <ImageSection>
        <ProductImage
          src="https://cdn.shapermint.com/assets/shapermint/images/SHM_Account_Login-img.jpg"
          alt="Shapermint Models"
          loading="lazy"
        />
      </ImageSection>

      <FormSection>
        <FormContainer>
          <Title>Create Your Account</Title>

          {error && <ErrorMessage>{error}</ErrorMessage>}

          <form onSubmit={handleSubmit}>
            <NameRow>
              <InputGroup>
                <Label htmlFor="firstName">First Name:</Label>
                <Input
                  id="firstName"
                  type="text"
                  placeholder="Jane"
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                />
                {fieldErrors.firstName && <FieldError>{fieldErrors.firstName}</FieldError>}
              </InputGroup>

              <InputGroup>
                <Label htmlFor="lastName">Last Name:</Label>
                <Input
                  id="lastName"
                  type="text"
                  placeholder="Doe"
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                />
                {fieldErrors.lastName && <FieldError>{fieldErrors.lastName}</FieldError>}
              </InputGroup>
            </NameRow>

            <InputGroup>
              <Label htmlFor="email">Email:</Label>
              <Input
                id="email"
                type="email"
                placeholder="email@example.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
              {fieldErrors.email && <FieldError>{fieldErrors.email}</FieldError>}
            </InputGroup>

            <InputGroup>
              <Label htmlFor="password">Password:</Label>
              <Input
                id="password"
                type="password"
                placeholder="At least 8 characters"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
              {fieldErrors.password && <FieldError>{fieldErrors.password}</FieldError>}
            </InputGroup>

            <InputGroup>
              <Label htmlFor="confirmPassword">Confirm Password:</Label>
              <Input
                id="confirmPassword"
                type="password"
                placeholder="Repeat your password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
              />
              {fieldErrors.confirmPassword && (
                <FieldError>{fieldErrors.confirmPassword}</FieldError>
              )}
            </InputGroup>

            <SubmitButton type="submit" disabled={isLoading}>
              {isLoading ? 'CREATING ACCOUNT...' : 'CREATE ACCOUNT'}
            </SubmitButton>
          </form>

          <LoginText>
            Already have an account?{' '}
            <LoginLink to="/login">Log in here.</LoginLink>
          </LoginText>
        </FormContainer>
      </FormSection>
    </PageContainer>
  );
}
